import { useMemo, useRef, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "@/api/errors";
import type { components } from "@/api/schema";
import { useCan } from "@/shared/auth";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { PageHeader } from "@/shared/components/PageHeader";
import { Pagination } from "@/shared/components/Pagination";
import { toast } from "@/shared/lib/toast";

import type { PromotionFilters as Filters } from "../api/query-keys";
import { useBulkArchive } from "../api/use-bulk-archive";
import { useBulkTransition } from "../api/use-bulk-transition";
import { useCampaigns } from "../api/use-campaigns";
import { useDeletePromotion } from "../api/use-delete-promotion";
import { usePromotions } from "../api/use-promotions";
import { useTransitionStatus } from "../api/use-transition-status";
import { BulkActionBar } from "../components/BulkActionBar";
import { PromotionFilters } from "../components/PromotionFilters";
import { PromotionFormModal } from "../components/PromotionFormModal";
import { PromotionTable } from "../components/PromotionTable";
import { activationReasonsFromError, formatActivationReasons } from "../lib/activation-reasons";
import { promotionsToCsv } from "../lib/promotions-csv";
import { downloadCsv } from "../lib/report-csv";

type Promotion = components["schemas"]["Promotion"];

interface PromotionsPageProps {
  /** Initial filters (routes pass `campaign_id` from the URL in F4). */
  initialFilters?: Filters;
  /** Row "edit" action — routes wire this to `/promotion/$promotionId` (F4). */
  onEditPromotion?: (promotion: Promotion) => void;
  /** Create success — routes navigate to the builder for the new id (F5). */
  onPromotionCreated?: (id: string) => void;
  /** Filter changes → the route writes the URL-backed keys back to `?…` (spec §5.4). */
  onFiltersChange?: (filters: Filters) => void;
}

export function PromotionsPage({
  initialFilters,
  onEditPromotion,
  onPromotionCreated,
  onFiltersChange,
}: PromotionsPageProps): JSX.Element {
  const { t } = useTranslation(["promotion", "nav"]);
  const canManage = useCan("promotions:manage");
  const [filters, setFilters] = useState<Filters>(initialFilters ?? {});
  // Mirrors `filters` so `updateFilters` can merge same-tick calls (PromotionFilters
  // fires date_from/date_to back-to-back from one onApply) without reading state
  // inside a setState updater — StrictMode double-invokes updaters in dev, and
  // `onFiltersChange` must not fire with them. `filters` is only ever set from
  // `updateFilters`, which writes both in lockstep, so the ref never drifts; it is
  // read/written from the event handler only, never mutated during render.
  const filtersRef = useRef(filters);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<Promotion | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const { data, isLoading, isError } = usePromotions({ ...filters, offset, limit });
  const { data: campaignData } = useCampaigns();
  const bulkTransition = useBulkTransition();
  const bulkArchive = useBulkArchive();
  const transition = useTransitionStatus();
  const del = useDeletePromotion();

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? items.length;
  const campaigns = (campaignData?.items ?? []).map((c) => ({ id: c.id, name: c.name }));
  // Seller filter options: distinct seller ids from the loaded rows (no BE list endpoint).
  const sellers = useMemo(() => [...new Set(items.map((p) => p.seller_id))].sort(), [items]);
  const bulkPending = bulkTransition.isPending || bulkArchive.isPending;

  function updateFilters<K extends keyof Filters>(key: K, value: Filters[K] | undefined): void {
    // Reads/writes `filtersRef` (not the `filters` closure, not a setState updater):
    // PromotionFilters fires date_from/date_to back-to-back in the same tick
    // (DateRangePicker's onApply), so the ref must be updated synchronously for the
    // second call to see the first's change. `onFiltersChange` runs here, outside
    // any updater, so StrictMode's dev double-invocation of updaters can't double-fire it.
    const next = { ...filtersRef.current };
    if (value === undefined) delete next[key];
    else next[key] = value;
    filtersRef.current = next;
    setFilters(next);
    setOffset(0);
    setSelected(new Set());
    onFiltersChange?.(next);
  }

  function toggleSelect(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]): void {
    setSelected((prev) => (ids.every((id) => prev.has(id)) ? new Set() : new Set(ids)));
  }

  const onErr = (err: unknown): void => toast.error(apiErrorMessage(err));
  const clearSel = (): void => setSelected(new Set());

  // 422 activation_invalid carries reasons[]; show them instead of the raw code.
  const onActivateErr =
    (name: string) =>
    (err: unknown): void => {
      const reasons = activationReasonsFromError(err);
      if (!reasons) return onErr(err);
      toast.error(
        [t("precheck.toast_blocked", { name }), ...formatActivationReasons(reasons, t)].join("\n"),
        { multiline: true },
      );
    };

  // BE transition matrix: draft→{active,archived}; active→finish; finish→{draft,archived}.
  // The only legal bulk transitions from the list are activate (draft→active) and
  // finish (active→finish); there is no active→draft "deactivate".
  function bulkSetStatus(target: "active" | "finish"): void {
    bulkTransition.mutate(
      { ids: [...selected], target },
      {
        onSuccess: clearSel,
        onError:
          target === "active"
            ? onActivateErr(t("picker.selected_count", { count: selected.size }))
            : onErr,
      },
    );
  }

  function bulkDoArchive(): void {
    bulkArchive.mutate({ ids: [...selected] }, { onSuccess: clearSel, onError: onErr });
  }

  function confirmDelete(): void {
    if (!deleteTarget) return;
    del.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
      onError: onErr,
    });
  }

  function handleExport(): void {
    downloadCsv("promotions-export.csv", promotionsToCsv(items));
    toast.success(t("export_done", { count: items.length }));
  }

  // Inline activate switch. BE matrix has NO active→draft: the only legal way to
  // take a live promotion down is active→finish. So toggling a draft on activates
  // it (draft→active) and toggling an active off finishes it (active→finish).
  // Finished/archived rows render locked, so only draft/active reach here.
  // State change only — firing waits for Phase 2 (see the Phase-2 banner).
  function handleActivateToggle(p: Promotion): void {
    const target = p.status === "active" ? "finish" : "active";
    transition.mutate(
      { id: p.id, target },
      {
        onSuccess: () =>
          toast.success(t(target === "active" ? "activate.activated" : "activate.finished")),
        onError: target === "active" ? onActivateErr(p.name) : onErr,
      },
    );
  }

  // Recover a finished promotion back to draft (finish→draft). The table only
  // enables this when quota_used === 0, so by the time we get here it's safe.
  function handleReopen(p: Promotion): void {
    transition.mutate(
      { id: p.id, target: "draft" },
      {
        onSuccess: () => toast.success(t("activate.reopened")),
        onError: onErr,
      },
    );
  }

  return (
    <div data-testid="promotions-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.promotion")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("promotion:page.title")}</span>
          </>
        }
        title={t("page.title")}
      />

      <PromotionFilters
        filters={filters}
        sellers={sellers}
        onChange={updateFilters}
        onExport={handleExport}
        {...(canManage ? { onCreate: () => setCreateOpen(true) } : {})}
      />

      <PromotionTable
        items={items}
        isLoading={isLoading}
        isError={isError}
        canManage={canManage}
        {...(onEditPromotion ? { onEdit: onEditPromotion } : {})}
        {...(canManage
          ? {
              selectedIds: selected,
              onToggleSelect: toggleSelect,
              onToggleSelectAll: toggleSelectAll,
              onActivateToggle: handleActivateToggle,
              pendingActivateId: transition.isPending ? transition.variables?.id : null,
              onReopen: handleReopen,
              onDelete: setDeleteTarget,
            }
          : {})}
      />

      {!isLoading && !isError && items.length > 0 && (
        <Pagination
          total={total}
          offset={offset}
          limit={limit}
          pageSizeOptions={[10, 25, 50, 100]}
          onChange={({ offset: nextOffset, limit: nextLimit }) => {
            setOffset(nextOffset);
            setLimit(nextLimit);
            setSelected(new Set());
          }}
        />
      )}

      <BulkActionBar
        count={selected.size}
        disabled={bulkPending}
        onActivate={() => bulkSetStatus("active")}
        onFinish={() => bulkSetStatus("finish")}
        onArchive={bulkDoArchive}
        onClear={clearSel}
      />

      {createOpen && (
        <PromotionFormModal
          campaigns={campaigns}
          {...(filters.campaign_id ? { defaultCampaignId: filters.campaign_id } : {})}
          onCreated={(id) => onPromotionCreated?.(id)}
          onClose={() => setCreateOpen(false)}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title={t("action.delete")}
          description={deleteTarget.name}
          confirmLabel={t("action.delete")}
          cancelLabel={t("bulk.clear")}
          isPending={del.isPending}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
